/**
 * Parent create form.
 * POSTs personal-account + contract in one submit.
 */
import * as React from 'react';
import { useCreateParentRemoteProfile } from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { useFormValidation } from '@/hooks/useFormValidation';
import { resolveContactCheckType, useContactExistsCheck } from '@/hooks/useContactExistsCheck';
import { parentCreateValidationSchema } from '@/utils/validation-schemas-utils';
import {
  areContactsIdentical,
  isPrimaryContactFormValid,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import type { ParentFormValues } from '@/types/parent.types';
import {
  buildCreateParentDto,
  defaultParentUpload,
  mergePendingUploads,
  emptyCreateParentForm,
  parentValidationPayload,
  useParentFormHandlers,
  type ParentContactType,
} from '@/components/parents/parent-form-utils';
import { ParentPersonalAccountSection } from '@/components/parents/parent-form-builder/ParentPersonalAccountSection';
import { ParentContractSection } from '@/components/parents/parent-form-builder/ParentContractSection';
import { buildPendingDocumentFilePatch } from '@/utils/pending-document-link-utils';
import { hasPendingUploadFiles, uploadPendingFiles } from '@/utils/pending-upload-utils';

/** Props for {@link CreateParentView}. */
interface CreateParentViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/** Parent create form. */
export function CreateParentView({ onClose, onSuccess }: Readonly<CreateParentViewProps>) {
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';
  const defaultCountry = useDefaultPhoneCountry();
  const validation = useFormValidation(parentCreateValidationSchema);

  const [formData, setFormData] = React.useState<ParentFormValues>(() =>
    emptyCreateParentForm(defaultCountry),
  );
  const [contactType, setContactType] = React.useState<ParentContactType>('unknown');
  const [pendingFiles, setPendingFiles] = React.useState<Record<string, File | null>>({});
  const [isUploading, setIsUploading] = React.useState(false);

  const { exists: contactExists, isChecking: isPrimaryChecking } = useContactExistsCheck({
    value: formData.contact ?? '',
    type: resolveContactCheckType(contactType),
    enabled: contactType !== 'unknown' && (formData.contact ?? '').trim().length > 0,
  });

  const secondaryContactType = contactType === 'email' ? 'phone' : 'email';
  const { exists: secondaryContactExists, isChecking: isSecondaryChecking } = useContactExistsCheck(
    {
      value: formData.secondaryContact ?? '',
      type: secondaryContactType,
      enabled: contactType !== 'unknown' && (formData.secondaryContact ?? '').trim().length > 0,
    },
  );

  const contactsAreIdentical = areContactsIdentical(
    formData.contact,
    formData.secondaryContact ?? '',
  );

  React.useEffect(() => {
    validation.validateInitialFormData(formData);
  }, []);

  const { handleChange, handleBlur, handleContactChange } = useParentFormHandlers({
    formData,
    setFormData,
    contactType,
    setContactType,
    defaultCountry,
    validation,
  });

  const createMutation = useCreateParentRemoteProfile();

  /** Store the file for submit upload and write a pending link so required docs validate. */
  const handleFileChange = (key: string, file: File | null) => {
    setPendingFiles((prev) => ({ ...prev, [key]: file }));
    const patch = buildPendingDocumentFilePatch(key, file);
    if (!patch) return;
    Object.entries(patch).forEach(([field, value]) => {
      handleChange(field, value);
    });
  };

  /** Validate, upload pending files, then POST personal-account + contract. */
  const handleSubmit = async () => {
    if (!validation.validateForm(parentValidationPayload(formData, contactType, contactExists)))
      return;

    if (hasPendingUploadFiles(pendingFiles)) {
      setIsUploading(true);
    }
    let uploadedUrls: Record<string, string> = {};
    try {
      uploadedUrls = await uploadPendingFiles(pendingFiles, defaultParentUpload);
    } catch (err) {
      toast.error(`Erreur lors de l'upload : ${getErrorMessage(err)}`);
      setIsUploading(false);
      return;
    }

    const enrichedData = mergePendingUploads(formData, uploadedUrls);

    createMutation.mutate(
      { dto: buildCreateParentDto(enrichedData, contactType, defaultCountry), companyId },
      {
        onSuccess: () => {
          toast.success('Parent créé avec succès');
          onSuccess?.();
        },
        onError: (err: unknown) => toast.error(getErrorMessage(err)),
      },
    );
    setIsUploading(false);
  };

  return (
    <CollapseReplaceForm
      formType="create"
      title="Créer un parent"
      subtitle="Renseignez les informations du parent"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={isUploading || createMutation.isPending}
      isValid={isPrimaryContactFormValid({
        primaryExists: contactExists,
        secondaryExists: secondaryContactExists,
        isPrimaryChecking,
        isSecondaryChecking,
        identical: contactsAreIdentical,
        formValid: validation.isFormValid(),
      })}
    >
      <ParentPersonalAccountSection
        formData={formData}
        onChange={handleChange}
        onBlur={handleBlur}
        validation={validation}
        mode="create"
        defaultCountry={defaultCountry}
        contactType={contactType}
        onContactChange={handleContactChange}
        contactExists={contactExists}
        secondaryContactExists={secondaryContactExists}
      />
      <ParentContractSection
        formData={formData}
        onChange={handleChange}
        onFileSelect={handleFileChange}
      />
    </CollapseReplaceForm>
  );
}
